import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import URL from 'config/url';
import axiosAdmin from 'utils/axiosAdmin';

export const getDefaultPlatformMarkup = createAsyncThunk('GET DEFAULT PLATFORM MARKUP', async (thunkAPI) => {
  const response = await axiosAdmin.get(URL.GET_DEFAULT_PLATFORM_MARKUP, thunkAPI);
  return response.data;
});

export const updateDefaultPlatformMarkup = createAsyncThunk(
  'UPDATE DEFAULT PLATFORM MARKUP',
  async (data, thunkAPI) => {
    const response = await axiosAdmin.patch(URL.GET_DEFAULT_PLATFORM_MARKUP, data, thunkAPI);
    return response.data;
  }
);

export const getServiceMarkup = createAsyncThunk('GET SERVICE MARKUP', async (thunkAPI) => {
  const response = await axiosAdmin.get(URL.GET_SERVICE_MARKUP, thunkAPI);
  return response.data;
});

export const updateServiceMarkup = createAsyncThunk('UPDATE SERVICE MARKUP', async (data, thunkAPI) => {
  const response = await axiosAdmin.patch(URL.GET_SERVICE_MARKUP, data, thunkAPI);
  return response.data;
});

export const getDefaultAgencyMarkup = createAsyncThunk('GET DEFAULT AGENCY MARKUP', async (thunkAPI) => {
  const response = await axiosAdmin.get(URL.DEFAULT_AGENCY_MARKUP, thunkAPI);
  return response.data;
});

export const updateDefaultAgencyMarkup = createAsyncThunk('UPDATE DEFAULT AGENCY MARKUP', async (data, thunkAPI) => {
  const response = await axiosAdmin.patch(URL.DEFAULT_AGENCY_MARKUP, data, thunkAPI);
  return response.data;
});

const formatQueryParam = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (Array.isArray(value)) return value.length > 0 ? value.join(',') : '';
  return String(value);
};

export const getAgencyMarkup = createAsyncThunk('GET AGENCY MARKUP', async (data, thunkAPI) => {
  const params = new URLSearchParams();
  params.set('page', data.page || 1);
  params.set('limit', data.limit || 10);

  const text = data.text || '';
  if (text) params.set('text', text);

  const subscriptionPlan = formatQueryParam(data.subscription_plan ?? data.subscriptionPlan);
  if (subscriptionPlan) params.set('subscription_plan', subscriptionPlan);

  const subscriptionStatus = formatQueryParam(data.subscription_status ?? data.subscriptionStatus);
  if (subscriptionStatus) params.set('subscription_status', subscriptionStatus);

  const paymentStatus = formatQueryParam(data.payment_status ?? data.paymentStatus);
  if (paymentStatus) params.set('payment_status', paymentStatus);

  const sortingField = data.sorting_field || data.sortingField;
  if (sortingField) params.set('sorting_field', sortingField);

  const sortingOrder = data.sorting;
  if (sortingOrder === 'ascend') params.set('sorting', 'asc');
  else if (sortingOrder === 'descend') params.set('sorting', 'desc');
  else if (sortingOrder === 'asc' || sortingOrder === 'desc') params.set('sorting', sortingOrder);

  const response = await axiosAdmin.get(`${URL.AGENCY_MARKUP}?${params.toString()}`, thunkAPI);
  return response.data;
});

export const updateAgencyMarkup = createAsyncThunk('UPDATE AGENCY MARKUP', async (data, thunkAPI) => {
  const response = await axiosAdmin.patch(URL.AGENCY_MARKUP, data, thunkAPI);
  return response.data;
});

const initialState = {
  isLoading: false,
  isUpdating: false,
  markups: null,
  platformMarkup: null,
  updatedMarkup: null,
};

export const markupSlice = createSlice({
  name: 'markup',
  initialState,
  reducers: {
    resetUpdatedMarkup: (state) => {
      state.updatedMarkup = null;
    },
  },
  extraReducers: {
    // get default platform markup
    [getDefaultPlatformMarkup.pending]: (state) => {
      state.isLoading = true;
      state.platformMarkup = null;
    },
    [getDefaultPlatformMarkup.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.platformMarkup = action.payload.data;
    },
    [getDefaultPlatformMarkup.rejected]: (state, action) => {
      state.isLoading = false;
      state.platformMarkup = null;
      toast.error(action?.payload?.message);
    },

    //update default platform markup

    [updateDefaultPlatformMarkup.pending]: (state) => {
      state.isLoading = true;
      state.updatedMarkup = null;
    },
    [updateDefaultPlatformMarkup.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.updatedMarkup = action.payload.data.markup;
    },
    [updateDefaultPlatformMarkup.rejected]: (state, action) => {
      state.isLoading = false;
      state.updatedMarkup = null;
      toast.error(action?.payload?.message);
    },

    //get service markup

    [getServiceMarkup.pending]: (state) => {
      state.isLoading = true;
      state.markups = null;
    },
    [getServiceMarkup.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.markups = action.payload.data;
    },
    [getServiceMarkup.rejected]: (state, action) => {
      state.isLoading = false;
      state.markups = null;
      toast.error(action?.payload?.message);
    },

    //update service markup

    [updateServiceMarkup.pending]: (state) => {
      state.isLoading = true;
    },
    [updateServiceMarkup.fulfilled]: (state) => {
      state.isLoading = true;
    },
    [updateServiceMarkup.rejected]: (state, action) => {
      state.isLoading = false;
      toast.error(action?.payload?.message);
    },

    //get default agency markup

    [getDefaultAgencyMarkup.pending]: (state) => {
      state.isLoading = true;
      state.markups = null;
    },
    [getDefaultAgencyMarkup.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.markups = action.payload.data;
    },
    [getDefaultAgencyMarkup.rejected]: (state, action) => {
      state.isLoading = false;
      state.markups = null;
      toast.error(action?.payload?.message);
    },

    //update default agency markup

    [updateDefaultAgencyMarkup.pending]: (state) => {
      state.isUpdating = true;
    },
    [updateDefaultAgencyMarkup.fulfilled]: (state, action) => {
      state.isUpdating = false;
      toast.success(action.payload?.message || 'Markup updated successfully');
    },
    [updateDefaultAgencyMarkup.rejected]: (state, action) => {
      state.isUpdating = false;
      toast.error(action?.payload?.message);
    },

    //get agency markup

    [getAgencyMarkup.pending]: (state) => {
      state.isLoading = true;
      state.markups = null;
    },
    [getAgencyMarkup.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.markups = action.payload.data;
    },
    [getAgencyMarkup.rejected]: (state, action) => {
      state.isLoading = false;
      state.markups = null;
      toast.error(action?.payload?.message);
    },

    //update agency markup

    [updateAgencyMarkup.pending]: (state) => {
      state.isUpdating = true;
      state.updatedMarkup = null;
    },
    [updateAgencyMarkup.fulfilled]: (state, action) => {
      state.isUpdating = false;
      const { markups } = state;
      const updatedRecord = action.payload?.data?.markup;
      if (updatedRecord && markups?.accountMarkups) {
        state.markups = {
          ...markups,
          accountMarkups: markups.accountMarkups.map((el) =>
            el?.id === updatedRecord.id ? { ...el, ...updatedRecord } : el
          ),
        };
      }
      toast.success(action.payload?.message || 'Markup updated successfully');
    },
    [updateAgencyMarkup.rejected]: (state, action) => {
      state.isUpdating = false;
      state.updatedMarkup = null;
      toast.error(action?.payload?.message);
    },

    //update default platform markup

    [updateDefaultPlatformMarkup.pending]: (state) => {
      state.isLoading = true;
      state.updatedMarkup = null;
    },
    [updateDefaultPlatformMarkup.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.updatedMarkup = action.payload.data.markup;
    },
    [updateDefaultPlatformMarkup.rejected]: (state, action) => {
      state.isLoading = false;
      state.updatedMarkup = null;
      toast.error(action?.payload?.message);
    },
  },
});

export const { resetUpdatedMarkup } = markupSlice.actions;
export default markupSlice.reducer;
